import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import routes from 'routes';
import { login } from 'api/authApi';
import {
  identifyMeetSignup,
  onboardMeetSignup,
  getPublicMeetMyStatus,
} from 'api/publicMeetSignupApi';
import { persistSession } from 'services/auth/tokenService';
import CustomNewButton from 'components/NewDesign/CustomNewButton';

import styles from './RegisterDialog.module.css';

// §6.6 / C2 — the registration dialog launched from the landing "Register athletes" CTA when the visitor is NOT logged
// in. Everything happens in place (no page navigation until they're authenticated):
//   email → identify
//     · known (real, email-confirmed account)  → password box → log in
//     · new / unconfirmed shell                → we've emailed a 6-digit code → enter code + name + password → onboard
// Either path ends authenticated; we then route by host-club membership (in-club/registered → My Meet Registrations;
// otherwise → the public registration wizard). A new account is platform-only until they register their first athlete.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RegisterDialog = ({ code, initialEmail = '', initialStep = 'email', onClose }) => {
  const navigate = useNavigate();

  const [step, setStep] = useState(initialStep); // email | password | onboard
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);

  // Once authenticated, route to the surface that matches who they are (§6.6, three cases):
  //   • brand-new (just onboarded) → the guided onboarding wizard (Case 3).
  //   • known to the host club      → the in-club My Registrations page (Case 1, AdminLayout).
  //   • known to portal, not club   → their personal-portal My Registrations (Case 2, PersonalLayout).
  const routeAfterAuth = async (isOnboard) => {
    let st = null;
    try {
      st = (await getPublicMeetMyStatus(code)).data;
    } catch {
      /* fall through */
    }
    if (st?.available) {
      if (isOnboard) {
        navigate(routes.personal.meetOnboard.url(st.meetId));
        return;
      }
      if (st.hasHostClubAccess || st.isRegistered) {
        navigate(routes.admin.myMeetRegistrations.url(st.clubId, st.meetId));
        return;
      }
      navigate(routes.personal.meetRegistrations.url(st.meetId));
      return;
    }
    // my-status unavailable (shouldn't happen for an open meet) — they're logged in, so their personal home.
    navigate(routes.personal.home);
  };

  const submitEmail = async () => {
    const e = email.trim();
    if (!EMAIL_RE.test(e)) {
      toast.error('Enter a valid email address');
      return;
    }
    setBusy(true);
    try {
      const res = await identifyMeetSignup(code, e);
      setStep(res.data?.status === 'known' ? 'password' : 'onboard');
    } catch (err) {
      toast.error(err.response?.data || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async () => {
    if (!password) {
      toast.error('Enter your password');
      return;
    }
    setBusy(true);
    try {
      const res = await login({ email: email.trim(), password });
      // Public flow never persists — forget on tab close. Persistence is opt-in, only via the Login page's "Remember me".
      persistSession(res.data, { remember: false, email: email.trim() });
      await routeAfterAuth(false); // existing account → manage (Case 1 or 2), never the onboarding wizard
    } catch (err) {
      toast.error(err.response?.data || 'Invalid email or password');
      setBusy(false);
    }
  };

  const resendCode = async () => {
    setBusy(true);
    try {
      await identifyMeetSignup(code, email.trim());
      toast.success('We’ve sent a new code to your email.');
    } catch (err) {
      toast.error(err.response?.data || 'Couldn’t resend the code. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const submitOnboard = async () => {
    if (!confirmationCode.trim()) {
      toast.error('Enter the code from your email');
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Enter your first and last name');
      return;
    }
    if (!newPassword) {
      toast.error('Choose a password');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords don’t match');
      return;
    }
    setBusy(true);
    try {
      const res = await onboardMeetSignup(code, {
        email: email.trim(),
        confirmationCode: confirmationCode.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password: newPassword,
      });
      // Public flow never persists — forget on tab close. Persistence is opt-in, only via the Login page's "Remember me".
      persistSession(res.data, { remember: false, email: email.trim() });
      await routeAfterAuth(true); // brand-new account → the guided onboarding wizard (Case 3)
    } catch (err) {
      toast.error(err.response?.data || 'Couldn’t complete your registration. Please try again.');
      setBusy(false);
    }
  };

  const startOver = () => {
    setStep('email');
    setPassword('');
    setConfirmationCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    // No close-on-outside-click (site rule): the dialog is dismissed only by the × button.
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
          ×
        </button>

        {step === 'email' && (
          <>
            <h2 className={styles.title}>Register your athletes</h2>
            <p className={styles.hint}>Enter your email to get started.</p>
            <label className={styles.label}>Email address</label>
            <input
              type="email"
              className={styles.input}
              value={email}
              placeholder="you@example.com"
              autoFocus
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !busy && submitEmail()}
            />
            <div className={styles.actions}>
              <CustomNewButton variant="primary2" onClick={submitEmail} disabled={busy}>
                {busy ? 'Checking…' : 'Continue'}
              </CustomNewButton>
            </div>
          </>
        )}

        {step === 'password' && (
          <>
            <h2 className={styles.title}>Welcome back</h2>
            <p className={styles.hint}>
              Enter your password to register for this meet.
              <br />
              <span className={styles.muted}>{email}</span>
            </p>
            <label className={styles.label}>Password</label>
            <input
              type="password"
              className={styles.input}
              value={password}
              autoFocus
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !busy && submitPassword()}
            />
            <div className={styles.actions}>
              <CustomNewButton variant="primary2" onClick={submitPassword} disabled={busy}>
                {busy ? 'Logging in…' : 'Log in'}
              </CustomNewButton>
            </div>
            <div className={styles.linkRow}>
              <button type="button" className={styles.linkBtn} onClick={() => navigate(routes.auth.forgotPassword)}>
                Forgot password?
              </button>
              <button type="button" className={styles.linkBtn} onClick={startOver} disabled={busy}>
                Use a different email
              </button>
            </div>
          </>
        )}

        {step === 'onboard' && (
          <>
            <h2 className={styles.title}>Check your email</h2>
            <p className={styles.hint}>
              We’ve sent a 6-digit code to <span className={styles.muted}>{email}</span>. Enter it below,
              then set up your account to finish.
            </p>
            <label className={styles.label}>Code</label>
            <input
              type="text"
              inputMode="numeric"
              className={styles.input}
              value={confirmationCode}
              placeholder="6-digit code"
              autoFocus
              onChange={(e) => setConfirmationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
            />
            <div className={styles.fieldRow}>
              <div className={styles.fieldHalf}>
                <label className={styles.label}>First name</label>
                <input
                  type="text"
                  className={styles.input}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className={styles.fieldHalf}>
                <label className={styles.label}>Last name</label>
                <input
                  type="text"
                  className={styles.input}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>
            <label className={styles.label}>Choose a password</label>
            <input
              type="password"
              className={styles.input}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <label className={styles.label}>Confirm password</label>
            <input
              type="password"
              className={styles.input}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !busy && submitOnboard()}
            />
            <div className={styles.actions}>
              <CustomNewButton variant="success" onClick={submitOnboard} disabled={busy}>
                {busy ? 'Finishing…' : 'Create account & continue'}
              </CustomNewButton>
            </div>
            <div className={styles.linkRow}>
              <button type="button" className={styles.linkBtn} onClick={resendCode} disabled={busy}>
                Resend code
              </button>
              <button type="button" className={styles.linkBtn} onClick={startOver} disabled={busy}>
                Use a different email
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RegisterDialog;
